import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router-dom";

import { ROUTE_CONFIG } from "@/routes/config";

import { skipsCheckout, useOnboardingVariant } from "@/utils/useOnboardingVariant";

import {
	useCreateCheckoutSessionMutation,
	useGetStripeProductsQuery,
} from "@/pages/Private/redux/subscription/subscription.api";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { OnboardingStepper } from "../components/OnboardingStepper";
import { PlanSelector } from "../../Subscription/components/PlanSelector/PlanSelector";
import { PaymentInterval } from "../../Subscription/components/PlanSelector/types";
import { usePlanCards } from "../../Subscription/components/PlanSelector/usePlanCards";

const POPULAR_CREDITS = 5000;

export const OnboardingSubscription: FunctionComponent = () => {
	const { t } = useTranslation();
	const { data: stripeProducts } = useGetStripeProductsQuery();
	const { user } = useAppSelector(authSelector);
	const allowInstallments = user?.company?.allowInstallments === true;

	// V2 users never check out (PREC-3719) — a stale CHECKOUT resume position
	// must not strand them on this page.
	const skipCheckout = skipsCheckout(useOnboardingVariant());

	const [paymentInterval, setPaymentInterval] = useState<PaymentInterval>("month");
	const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
	const [checkoutError, setCheckoutError] = useState<string | null>(null);

	const availableIntervals = useMemo<PaymentInterval[]>(() => {
		const base: PaymentInterval[] = ["month", "year"];

		return allowInstallments ? [...base, "semi-annual", "quarter"] : base;
	}, [allowInstallments]);

	const { plans, getPriceId } = usePlanCards(stripeProducts, paymentInterval);

	// Default to the recommended (popular) plan and keep the selection valid
	// when the interval changes the set of available plans.
	useEffect(() => {
		if (!plans.length) {
			return;
		}

		if (plans.some((plan) => plan.productId === selectedProductId)) {
			return;
		}

		const popular = plans.find((plan) => plan.credits === POPULAR_CREDITS);

		setSelectedProductId((popular ?? plans[0]).productId);
	}, [plans, selectedProductId]);

	const [createCheckoutSession, { isLoading: isCreatingCheckoutSession }] =
		useCreateCheckoutSessionMutation();

	const getFPTid = () => {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		return (window as any).FPROM?.data?.tid;
	};

	const handleCreateCheckoutSession = async () => {
		if (!selectedProductId) {
			return;
		}

		const priceId = getPriceId(selectedProductId);

		if (!priceId) {
			return;
		}

		setCheckoutError(null);

		try {
			const { sessionUrl } = await createCheckoutSession({
				priceId,
				productId: selectedProductId,
				fptid: getFPTid(),
			}).unwrap();

			window.location.href = sessionUrl;
		} catch (err: unknown) {
			const error = err as { status?: number; data?: { message?: string } };

			if (error?.status === 403) {
				setCheckoutError(
					error?.data?.message || t("usage.subscription.select.installmentsNotAllowed")
				);
			}
		}
	};

	const heading = (
		<div className="text-center text-md font-bold mb-2 text-text">
			{t("usage.subscription.onboarding.headingIntro")}
			<div className="text-transparent bg-gradient-to-r from-blue-action to-gradient-brand-to bg-clip-text font-bold">
				{t("usage.subscription.onboarding.headingBrand")}
			</div>
		</div>
	);

	const footerNote = (
		<>
			{t("usage.subscription.select.trialNotice")}
			<br />
			<span className="font-bold">{t("usage.subscription.footnote.vat")}</span>
			{paymentInterval === "year" && (
				<>
					<br />
					<span className="font-bold">{t("usage.subscription.footnote.yearUpfront")}</span>
				</>
			)}
			{paymentInterval === "quarter" && (
				<>
					<br />
					<span className="font-bold">{t("usage.subscription.footnote.quarterCommitment")}</span>
				</>
			)}
			{paymentInterval === "semi-annual" && (
				<>
					<br />
					<span className="font-bold">{t("usage.subscription.footnote.semiAnnualCommitment")}</span>
				</>
			)}
		</>
	);

	if (skipCheckout) {
		return <Navigate to={ROUTE_CONFIG.ONBOARDING_DONE} replace />;
	}

	return (
		<PlanSelector
			activeInterval={paymentInterval}
			availableIntervals={availableIntervals}
			ctaDisabled={!selectedProductId}
			errorMessage={checkoutError}
			footerNote={footerNote}
			footerSlot={<OnboardingStepper step={4} />}
			heading={heading}
			isCtaLoading={isCreatingCheckoutSession}
			isLoading={stripeProducts === undefined}
			plans={plans}
			primaryCtaLabel={t("usage.subscription.onboarding.cta")}
			selectedProductId={selectedProductId}
			onIntervalChange={setPaymentInterval}
			onPrimaryCta={handleCreateCheckoutSession}
			onSelect={setSelectedProductId}
		/>
	);
};
